import React, { useState, useCallback } from 'react';
import { useNotesStore } from '../store';
import api from '../api';
import { Upload, File, Image, FileText, Code, Trash2, ExternalLink } from 'lucide-react';

const FILE_ICONS = {
  'image/': Image,
  'application/pdf': FileText,
  'text/': FileText,
  'application/json': Code,
  'text/javascript': Code,
  'text/typescript': Code
};

function getFileIcon(mimeType) {
  if (!mimeType) return File;
  for (const [prefix, Icon] of Object.entries(FILE_ICONS)) {
    if (mimeType.startsWith(prefix)) return Icon;
  }
  return File;
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function FileUpload({ noteId, onFileSelect }) {
  const { currentNote } = useNotesStore();
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const loadFiles = async () => {
    const { files: fileList } = await api.listFiles(noteId);
    setFiles(fileList);
  };

  React.useEffect(() => {
    loadFiles();
  }, [noteId]);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    await uploadFiles(droppedFiles);
  }, [noteId]);

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    await uploadFiles(selectedFiles);
  };

  const uploadFiles = async (filesToUpload) => {
    setIsUploading(true);

    for (const file of filesToUpload) {
      try {
        await api.uploadFile(file, noteId);
      } catch (error) {
        console.error('Upload failed:', error);
      }
    }

    await loadFiles();
    setIsUploading(false);
  };

  const handleDelete = async (fileId) => {
    if (confirm('Delete this file?')) {
      await api.deleteFile(fileId);
      await loadFiles();
    }
  };

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isDragging
            ? 'border-accent bg-accent/10'
            : 'border-dark-600 hover:border-dark-500'
        }`}
      >
        <Upload size={24} className="mx-auto mb-2 text-dark-400" />
        <p className="text-sm text-dark-400">
          Drag & drop files here, or{' '}
          <label className="text-accent hover:text-accent-light cursor-pointer">
            browse
            <input
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </label>
        </p>
        <p className="text-xs text-dark-500 mt-1">
          PDFs, images, documents, code files
        </p>
      </div>

      {/* Upload progress */}
      {isUploading && (
        <div className="flex items-center gap-2 text-sm text-dark-400">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent"></div>
          Uploading...
        </div>
      )}

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-dark-300">
            Uploaded Files ({files.length})
          </h4>
          {files.map((file) => {
            const Icon = getFileIcon(file.mime_type);
            return (
              <div
                key={file.id}
                className="flex items-center gap-3 p-2 bg-dark-800 rounded-lg group"
              >
                <Icon size={16} className="text-dark-400" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-dark-200 truncate">
                    {file.original_name}
                  </p>
                  <p className="text-xs text-dark-500">
                    {formatSize(file.size || 0)}
                  </p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <a
                    href={`/api/upload/${file.id}/download`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 text-dark-400 hover:text-dark-200"
                  >
                    <ExternalLink size={14} />
                  </a>
                  <button
                    onClick={() => handleDelete(file.id)}
                    className="p-1 text-dark-400 hover:text-red-400"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
